import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/shadcn";
import { ArrowClockwiseIcon, WarningIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useOutlineChangeStatus } from "../hooks/useOutlineChangeStatus";
import { usePresentationChatStore } from "../hooks/usePresentationChat";
import { getTooltipConfig } from "../const/consts";

type TRefreshOutlineButtonProps = {
  outlineGeneratedAt: string | null;
  hasSlides: boolean;
  onRefresh: () => void;
  isRefreshing: boolean;
  disabled: boolean;
};

export function RefreshOutlineButton({
  outlineGeneratedAt,
  hasSlides,
  onRefresh,
  isRefreshing,
  disabled
}: TRefreshOutlineButtonProps) {
  const { t } = useTranslation("projectHome");
  const { id: projectId } = useParams() as { id: string };
  const status = useOutlineChangeStatus(outlineGeneratedAt, hasSlides);
  const hasChanges = status !== "up_to_date" && status !== "no_outline";
  const config = getTooltipConfig(t)[status];
  const isChatRefreshing = usePresentationChatStore(
    (s) => !!(s.loadingStates[projectId]?.loadingMessageId)
  );

  const handleRefresh = useCallback(() => {
    onRefresh();
  }, [onRefresh]);

  const isDisabled = disabled || isChatRefreshing || (!hasChanges && !isRefreshing);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn(isDisabled && "cursor-not-allowed")}>
          <AppButton
            appearance="outlined"
            color="zinc150"
            onClick={handleRefresh}
            disabled={isDisabled}
            isLoading={isRefreshing}
            loadingText={t("presentation.refreshing")}
            data-testid="presentation-refresh-outline"
            startAdornment={<ArrowClockwiseIcon size={16} />}
            endAdornment={hasChanges && !isRefreshing ? <WarningIcon size={16} /> : undefined}
            className={cn("min-w-[168px]", isDisabled && "pointer-events-none")}
          >
            {t("presentation.refreshOutline")}
          </AppButton>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end" sideOffset={8} className="w-[255px] p-0">
        <div className="flex gap-3.5 items-start pr-3.5 py-3">
          <div className={cn("w-[2px] min-h-[23px] self-stretch rounded-r-sm shrink-0", config.barColor)} />
          <div className="flex flex-col gap-1.5 py-0.5 flex-1 min-w-0">
            <FigmaTypography variant="label-2" color="zinc900">
              {config.title}
            </FigmaTypography>
            <FigmaTypography variant="body-3" color="zinc600">
              {config.description}
            </FigmaTypography>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
